import React from "react";
import { pathHex, sphereHex, sphereQuadrants } from "./lib/colorScales";
import { inkFor, inkForMany, haloFor } from "./lib/contrast";
import { PLATE_H, PLATE_W, markerLabelPosition, nameAnchor } from "./lib/labelGeometry";
import { UNSCALED_SPHERE } from "./lib/palette";
import { isNegativeVeil } from "./lib/veilGeometry";
import { isSelected as matches } from "./lib/selection";

// EVERY text element in the diagram lives here, in one group painted last.
// SVG has no z-index, so render order IS the design, and labels scattered
// through the shape groups is what let path plates end up under path lines.
//
// Paint order, top of TreeDiagram to bottom:
//   1 overlay regions and columns   4 overlay hulls
//   2 marker rules                  5 spheres
//   3 paths                         6 THIS — every label
//
// Every size and anchor below comes from lib/labelGeometry, which is also what
// the caption solver measures against. No knockout plates on NAMES: a knockout
// guarantees the name is legible by destroying whatever is beneath it, which is
// fine over a line and not fine over another label.

function SphereNumeral({ sephirah, scale, radius, inscription }) {
  const counted = sephirah.countedInTen !== false;
  const fill = sphereHex(sephirah, scale) || sephirah.colorHex || UNSCALED_SPHERE;
  const quadrants = sphereQuadrants(sephirah, scale);
  const quartered = quadrants.length === 4;
  const ink = quartered ? inkForMany(quadrants) : inkFor(fill);

  if (!counted) {
    return (
      <text
        className="sphere__inner-name"
        x={sephirah.x}
        y={sephirah.y + 0.9}
        textAnchor="middle"
        fill={fill}
        aria-hidden="true"
        data-testid={`numeral-${sephirah.slug}`}
      >
        {sephirah.name}
      </text>
    );
  }

  const hasHebrewName = inscription === "hebrew" && sephirah.hebrew;
  const hasPlanetSymbol = inscription === "planet" && sephirah.planetSymbol;
  const content = hasHebrewName
    ? sephirah.hebrew
    : hasPlanetSymbol
      ? sephirah.planetSymbol + "\uFE0E"
      : sephirah.number;
  const variantClass = hasHebrewName
    ? " sphere__hebrew-name"
    : hasPlanetSymbol
      ? " sphere__planet-symbol"
      : "";

  return (
    <text
      className={`sphere__number${variantClass}`}
      x={sephirah.x}
      y={sephirah.y + 1.2}
      textAnchor="middle"
      fill={ink}
      stroke={quartered ? haloFor(ink) : "none"}
      data-ink={ink}
      data-testid={`numeral-${sephirah.slug}`}
      lang={hasHebrewName ? "he" : undefined}
      aria-hidden="true"
    >
      {content}
    </text>
  );
}

function SphereName({ sephirah, radius, dimmed }) {
  const place = nameAnchor(sephirah, radius);

  return (
    <g className={dimmed ? "label label--dim" : "label"} data-testid={`name-${sephirah.slug}`}>
      <text
        className="sphere__name"
        x={place.x}
        y={place.y}
        textAnchor={place.anchor}
        aria-hidden="true"
      >
        {sephirah.name}
      </text>
    </g>
  );
}

function PathLabel({ path, colour, dimmed, lit }) {
  const { labelX, labelY, hebrewLetter, number, slug } = path;
  return (
    <g
      className={`label path__label${dimmed ? " label--dim" : ""}${lit ? " label--lit" : ""}`}
      data-testid={`plate-${slug}`}
      aria-hidden="true"
    >
      {/* Knocked out, so the letter stays legible wherever a line runs beneath. */}
      <rect
        className="path__plate"
        x={labelX - PLATE_W / 2}
        y={labelY - PLATE_H / 2}
        width={PLATE_W}
        height={PLATE_H}
        rx={1}
        stroke={colour}
      />
      <text
        className="path__letter"
        x={labelX}
        y={labelY - 0.2}
        textAnchor="middle"
        fill={colour}
        lang="he"
      >
        {hebrewLetter}
      </text>
      <text className="path__number" x={labelX} y={labelY + 2.4} textAnchor="middle">
        {number}
      </text>
    </g>
  );
}

export { nameAnchor };

export default function LabelLayer({
  sephiroth,
  paths,
  radius,
  scale,
  showNames,
  showPathLabels,
  dimmedPaths,
  litPaths,
  markerGroups,
  viewBox,
  sphereInscription,
  selected = null,
  onSelectMarker = null,
  children
}) {
  return (
    <g className="tree__labels" data-testid="label-layer">
      {showPathLabels &&
        paths.map((path) => (
          <PathLabel
            key={path.slug}
            path={path}
            colour={pathHex(path, scale)}
            dimmed={dimmedPaths.has(path.slug)}
            lit={litPaths.has(path.slug)}
          />
        ))}

      {sephiroth.map((sephirah) => (
        <SphereNumeral
          key={`numeral-${sephirah.slug}`}
          sephirah={sephirah}
          scale={scale}
          radius={radius}
          inscription={sphereInscription}
        />
      ))}

      {/* An uncounted node carries its name INSIDE the circle, so it gets no
          outer label — otherwise it is written twice. */}
      {showNames &&
        sephiroth
          .filter((s) => s.countedInTen !== false)
          .map((sephirah) => (
            <SphereName
              key={`name-${sephirah.slug}`}
              sephirah={sephirah}
              radius={radius}
            />
          ))}

      {/* A marker's name is its control. It used to be a link that navigated
          away to the research page; selecting it writes the same commentary
          into the detail panel instead, which is where every other node on
          this diagram puts what it has to say. The research page is still one
          click further on, from inside the panel. */}
      {markerGroups.map(({ group, colour }) => {
        const position = markerLabelPosition(group, viewBox);
        const selectedHere = matches(selected, "marker", group.slug);
        const label = (
          <text
            className={`overlay__marker-label${isNegativeVeil(group) ? " overlay__negative-veil-label" : ""}${
              group.markerX === null || group.markerX === undefined ? "" : " overlay__marker-point-label"
            }`}
            x={position.x}
            y={position.y}
            textAnchor={position.anchor}
            fill={colour}
            stroke="var(--ground)"
            data-testid={`marker-label-${group.slug}`}
          >
            {group.name}
          </text>
        );
        if (!onSelectMarker) return React.cloneElement(label, { key: `marker-${group.slug}` });
        return (
          <g
            key={`marker-${group.slug}`}
            className={`overlay__marker-control${selectedHere ? " overlay__marker-control--selected" : ""}`}
            role="button"
            tabIndex={0}
            aria-pressed={selectedHere}
            aria-label={`${group.name}. ${group.translation || group.note || ""}`.trim()}
            onClick={(event) => {
              event.stopPropagation();
              onSelectMarker(group);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onSelectMarker(group);
            }}
            data-testid={`marker-link-${group.slug}`}
          >
            {label}
          </g>
        );
      })}

      {children}
    </g>
  );
}
